import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { ArrowRight, ChevronRight } from "lucide-react";

const TOC = [
  { id: "comparison", label: "Quick Comparison Table" },
  { id: "reviews", label: "Detailed Reviews" },
  { id: "buying-tips", label: "How to Choose a Portable Panel" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Best Portable Solar Panels (2026)",
  "description": "Find the best portable solar panels for camping, van life, solar generators, and emergency backup. Compare foldable and briefcase-style panels.",
  "url": "https://highestefficiencysolarcell.com/best-portable-solar-panels",
  "dateModified": "2026-06-01",
  "author": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
};

export default function BestPortablePanels() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const portableProducts = products.filter(
    (p) => p.best_for_category === "Best Portable Solar Panel" ||
           p.form_factor === "Portable" || p.form_factor === "Foldable" ||
           p.best_use_case?.toLowerCase().includes("camping")
  );

  const displayProducts = portableProducts.length > 0 ? portableProducts : products.slice(0, 6);

  return (
    <>
      <SEOHead
        title="Best Portable Solar Panels 2026 | Foldable & Lightweight Picks"
        description="Find the best portable solar panels for camping, van life, and solar generators. Compare foldable panels, briefcase-style panels, and lightweight options."
        canonical="https://highestefficiencysolarcell.com/best-portable-solar-panels"
        schema={SCHEMA}
      />
    <div className="pb-28 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-accent">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground">Best Portable Solar Panels</span>
        </nav>

        <div className="max-w-4xl mb-2">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
            Best Portable Solar Panels (2026)
          </h1>
          <p className="text-lg text-muted-foreground mb-6 max-w-3xl">
            Portable solar panels are essential for camping, van life, emergency backup, and charging solar
            generators on the go. Here are our top picks.
          </p>
          <AffiliateDisclosure />
          <TableOfContents items={TOC} />
        </div>

        <section id="comparison" className="mb-8">
          <h2 className="font-heading font-bold text-3xl mb-4">Quick Comparison Table</h2>
        </section>

        {displayProducts.length > 0 && (
          <>
            <ComparisonTable products={displayProducts} />
            <div className="mt-16" id="reviews">
              <h2 className="font-heading font-bold text-3xl mb-8">Detailed Portable Panel Reviews</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </>
        )}

        <div className="mt-16 max-w-3xl" id="buying-tips">
          <h2 className="font-heading font-bold text-2xl mb-6">How to Choose a Portable Solar Panel</h2>
          <div className="space-y-5 text-muted-foreground">
            <p>
              <strong className="text-foreground">Weight vs. wattage trade-off.</strong> Higher wattage means more
              power but also more weight and bulk. For backpacking and hiking, stick to 20–60W panels (under 2 kg).
              For car camping or overlanding, 100–200W foldable panels (3.5–8 kg) are ideal. For base camp or
              extended van trip use, 200–400W setups make sense where weight is not a constraint.
            </p>
            <p>
              <strong className="text-foreground">Foldable vs. suitcase vs. rollable.</strong> Foldable fabric panels
              are the most popular — they fold in half, pack into a slim case, and weigh less than suitcase-style
              units. Suitcase-style panels (two rigid panels hinged together) are more durable and often include a
              built-in kickstand, but they're heavier and bulkier. Rollable panels are the newest category — extremely
              lightweight but lower efficiency and more fragile.
            </p>
            <p>
              <strong className="text-foreground">Power station compatibility is critical.</strong> Before buying,
              verify your panel's output voltage (Voc) is within your power station's rated solar input voltage range.
              Most foldable panels output 17–22V. Most portable power stations accept 12–28V or 12–60V input.
              Exceeding the voltage limit can damage your power station. Also confirm the connector type — most
              panels use MC4, while power stations use XT60 (Jackery, EcoFlow), Anderson (Bluetti), or DC barrel
              connectors. Always buy the correct adapter.
            </p>
            <p>
              <strong className="text-foreground">Efficiency matters more in portable panels.</strong> A highly
              efficient portable panel (23%+) produces more watts from a smaller, lighter panel — which is exactly
              what you want when portability is the priority. The best foldable panels from Jackery (23.7% HJT) and
              BougeRV (23.4% HJT) outperform budget panels by a meaningful margin in the same footprint.
            </p>
            <p>
              <strong className="text-foreground">Use the integrated kickstand.</strong> Most foldable panels include
              a built-in kickstand or angled support legs. Using them to tilt the panel toward the sun adds 20–40%
              more output compared to laying the panel flat. Adjust the angle throughout the day as the sun moves
              for maximum daily output.
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <h3 className="font-heading font-bold text-xl text-foreground">Frequently Asked Questions</h3>
            {[
              { q: "What's the most efficient portable solar panel in 2026?", a: "The Jackery SolarSaga 100W claims 23.7% efficiency using HJT cells — among the highest available in a foldable portable panel. The BougeRV 200W foldable achieves 23.4% efficiency and offers excellent value at the 200W size." },
              { q: "Can I use a portable solar panel while driving?", a: "Yes. Many campers place a foldable portable panel on the dashboard or roof of their vehicle to charge a power station while driving. Ensure the panel is secured safely and not blocking visibility. Some van lifers use magnetic roof mounts for hands-free panel deployment during drives." },
              { q: "How many watts of portable solar do I need for camping?", a: "For a basic camping setup (phone, tablet, LED lights, small fan), 100W is usually sufficient. For a portable power station running a mini fridge, CPAP, or laptop, 200W is the recommended minimum. Use our solar calculator for a precise recommendation based on your specific loads." },
            ].map((faq, i) => (
              <div key={i} className="border border-border rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-2 text-foreground text-base">{faq.q}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <Link
            to="/solar-panel-calculator"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
          >
            Calculate Your Needs <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/best-solar-panels-for-rv"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
          >
            Best for RV
          </Link>
        </div>
      </div>
      <EmailCapture source="portable-panels" />
    </div>
    </>
  );
}